import { useCallback, useState, useEffect, useRef } from "react";
import { Box, Flex, Text, keyframes } from "@chakra-ui/react";
import { CircleCheck, CircleAlert, Info } from "lucide-react";
import { useAnimationToggle } from "../Contexts/AnimationContext";

const VARIANTS = {
  success: { bg: "green.500", icon: CircleCheck },
  error: { bg: "red.500", icon: CircleAlert },
  warning: { bg: "orange.500", icon: CircleAlert },
  info: { bg: "blue.500", icon: Info },
};

const DURATIONS = {
  success: 4000,
  error: 6000,
  warning: 5000,
  info: 4000,
};

const popIn = keyframes`
  0% { opacity: 0; transform: translateX(-50%) translateY(16px) scale(0.92); }
  50% { opacity: 1; transform: translateX(-50%) translateY(-4px) scale(1.02); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
`;

const fadeOut = keyframes`
  0% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
  100% { opacity: 0; transform: translateX(-50%) translateY(8px) scale(0.96); }
`;

function KioskToastDisplay({ toast, onDismiss }) {
  const [phase, setPhase] = useState("enter"); // "enter" | "exit"
  const timerRef = useRef(null);
  const { animationsEnabled } = useAnimationToggle();

  useEffect(() => {
    if (toast.persistent) return;

    timerRef.current = setTimeout(() => {
      setPhase("exit");
      setTimeout(onDismiss, animationsEnabled ? 300 : 0);
    }, DURATIONS[toast.status] || 4000);

    return () => clearTimeout(timerRef.current);
  }, [toast, onDismiss, animationsEnabled]);

  const handleTap = () => {
    clearTimeout(timerRef.current);
    setPhase("exit");
    setTimeout(onDismiss, animationsEnabled ? 300 : 0);
  };

  const variant = VARIANTS[toast.status] || VARIANTS.info;
  const Icon = variant.icon;

  const animationStyle = animationsEnabled
    ? phase === "enter"
      ? `${popIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards`
      : `${fadeOut} 0.3s ease-in forwards`
    : undefined;

  return (
    <Box
      position="fixed"
      bottom="100px"
      left="50%"
      zIndex={1500}
      transform="translateX(-50%)"
      opacity={animationsEnabled ? undefined : phase === "enter" ? 1 : 0}
      sx={animationStyle ? { animation: animationStyle } : undefined}
      onClick={handleTap}
      cursor="pointer"
      maxW="90%"
      w="auto"
    >
      <Flex
        bg={variant.bg}
        color="white"
        rounded="full"
        px={6}
        py={3}
        align="center"
        gap={3}
        shadow="lg"
        minH="56px"
      >
        <Box as={Icon} boxSize={6} flexShrink={0} aria-hidden="true" />
        <Text fontSize="xl" fontWeight={600} lineHeight="short">
          {toast.message}
        </Text>
      </Flex>
    </Box>
  );
}

export function useKioskToast() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const show = useCallback((status, message, { persistent = false } = {}) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, status, message, persistent }]);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toastElements = toasts.map((toast) => (
    <KioskToastDisplay
      key={toast.id}
      toast={toast}
      onDismiss={() => dismiss(toast.id)}
    />
  ));

  return { show, toastElements };
}
